import * as React from "react";

import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { productFileClient } from "@v2/trpc/routes/productFile";
import { uploadToCloudflare } from "@v2/utils/cloudflareUpload";
import { ReploError } from "schemas/errors";

class ProjectIdRequiredError extends ReploError {}

interface PendingProductFile {
  id: string;
  url: string;
  name: string;
  sizeBytes: number;
  fileExtension: string;
  label?: string;
}

export const useProductFileUpload = () => {
  const [isUploading, setIsUploading] = React.useState(false);
  const [isError, setIsError] = React.useState(false);
  const [progressByFileId, setProgressByFileId] = React.useState<
    Record<string, number>
  >({});
  const cancelByFileIdRef = React.useRef<Record<string, () => void>>({});
  const activeUploadsRef = React.useRef(0);
  const projectId = useCurrentProjectId();

  const uploadProductFile = React.useCallback(
    async ({
      fileId,
      file,
      label,
    }: {
      fileId: string;
      file: File;
      label?: string;
    }): Promise<PendingProductFile> => {
      if (!projectId) {
        throw new ProjectIdRequiredError({
          message: "Project ID is required to upload product files.",
        });
      }

      const fileExtension = file.name.split(".").at(-1) ?? "";

      // mark upload started
      activeUploadsRef.current += 1;
      setIsUploading(true);
      setIsError(false);

      try {
        const fileUrl = await uploadToCloudflare({
          file,
          filename: file.name,
          projectId,
          fileId,
          fetchSignedUrl: (params) => {
            return productFileClient.fetchSignedUrl.query({
              ...params,
              fileId: params.fileId ?? crypto.randomUUID(),
            });
          },
          errorContext: "product file",
          onProgress: (loaded, total) => {
            const percentage = Math.round((loaded / total) * 100);
            setProgressByFileId((prev) => ({ ...prev, [fileId]: percentage }));
          },
          setCancelHandler: (cancel) => {
            cancelByFileIdRef.current[fileId] = cancel;
          },
        });

        const pendingFile: PendingProductFile = {
          id: fileId,
          url: fileUrl,
          name: file.name,
          sizeBytes: file.size,
          fileExtension,
          label,
        };

        activeUploadsRef.current = Math.max(0, activeUploadsRef.current - 1);
        if (activeUploadsRef.current === 0) {
          setIsUploading(false);
        }
        setProgressByFileId((prev) => {
          const next = { ...prev };
          delete next[fileId];
          return next;
        });
        delete cancelByFileIdRef.current[fileId];
        return pendingFile;
      } catch (error) {
        setIsError(true);
        activeUploadsRef.current = Math.max(0, activeUploadsRef.current - 1);
        if (activeUploadsRef.current === 0) {
          setIsUploading(false);
        }
        setProgressByFileId((prev) => {
          const next = { ...prev };
          delete next[fileId];
          return next;
        });
        delete cancelByFileIdRef.current[fileId];
        throw error;
      }
    },
    [projectId],
  );

  const cancelUpload = React.useCallback((fileId: string) => {
    cancelByFileIdRef.current[fileId]?.();
  }, []);

  return {
    uploadProductFile,
    isUploading,
    isError,
    progressByFileId,
    cancelUpload,
  };
};
